import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { img } from "@/api";
import { CANONICAL_ORIGIN, cleanPath, buildOrganizationLd, buildWebsiteLd } from "@/lib/seo";

/**
 * Central SEO head manager (react-helmet-async).
 * Renders exactly ONE canonical + ONE robots tag, Open Graph + Twitter cards,
 * and JSON-LD (page-specific + site-wide Organization/WebSite on the homepage).
 *
 * Props:
 *  - title, description, canonical, image, type
 *  - index  (default true)  -> index | noindex
 *  - follow (default true)  -> follow | nofollow
 *  - jsonLd (object | array of objects)
 */
export default function Seo({ title, description, canonical, image, type = "website", index = true, follow = true, jsonLd }) {
  const site = useSite();
  const location = useLocation();
  const s = (site && site.site) || {};
  const defaults = s.seo_defaults || {};

  const finalTitle = title || s.tab_title || defaults.default_title || "Ekta Wire Machines Pvt. Ltd.";
  const finalDesc = description || defaults.default_description || "";
  const finalImg = img(image || defaults.og_image || "");
  const path = cleanPath((location && location.pathname) || "/");
  const url = canonical || `${CANONICAL_ORIGIN}${path}`;
  const robots = `${index ? "index" : "noindex"},${follow ? "follow" : "nofollow"}`;
  const siteName = defaults.site_name || s.brand_name || "Ekta Wire Machines Pvt. Ltd.";

  // Assemble JSON-LD blocks: page-specific first, plus site-wide on the homepage.
  let blocks = [];
  if (Array.isArray(jsonLd)) blocks = jsonLd.filter(Boolean);
  else if (jsonLd) blocks = [jsonLd];
  if (path === "/") {
    blocks = [buildOrganizationLd(s), buildWebsiteLd(s), ...blocks];
  }

  return (
    <Helmet>
      <title>{finalTitle}</title>
      {finalDesc && <meta name="description" content={finalDesc} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={finalTitle} />
      {finalDesc && <meta property="og:description" content={finalDesc} />}
      {finalImg && <meta property="og:image" content={finalImg} />}
      <meta property="og:url" content={url} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      {finalDesc && <meta name="twitter:description" content={finalDesc} />}
      {finalImg && <meta name="twitter:image" content={finalImg} />}
      {defaults.twitter_handle && <meta name="twitter:site" content={defaults.twitter_handle} />}

      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </Helmet>
  );
}
